import { AuthController } from "@controllers/Auth.controller";
import { ExcelController } from "@controllers/Excel.controller";
import { OrganizationManager, type Team } from "@entities";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditIcon from "@mui/icons-material/Edit";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import Groups2Icon from "@mui/icons-material/Groups2";
import PersonPinRoundedIcon from "@mui/icons-material/PersonPinRounded";
import { CircularProgress, Stack } from "@mui/material";
import { organizationManagerRepo } from "@repositories";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { type ActionButtonsGetter } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import { type SingleRowActionGetter } from "@shared/frontend/hyper-table/inner-components/hyper-table-menu-actions-column/HyperTableMenuActionsColumn";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { useCallback, useMemo, useState } from "react";
import { type EntityFilter } from "remult";
import * as yup from "yup";
import { TeamsSelectDialog } from "../../../components/teams-select";
import { useAlert } from "../../../contexts/AlertProvider";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { useUser } from "../../../contexts/UserProvider";
import { useFormDialog } from "../../../dialog-components/FormDialog";
import useQuestion from "../../../dialog-components/QuestionDialog";
import { formatPhoneNumber } from "../../../generic-functions/format-phone-number";
import { downloadExcelFile } from "../../../helpers/excel/export";
import { useCommunities } from "../../../queries/communities.query";
import { useAddEditManager } from "../../../queries/users.query";
import { TableHeaderTitles } from "../table-header/TableHeader";
import { getColumns } from "./columns";
import filters from "./filters";

const ManagersTable = () => {
  const { HyperTable } = useCreateHyperTable(OrganizationManager);
  const { currOrganization } = useOrganization();
  const [teamsAnchor, setTeamsAnchor] = useState<EventTarget | null>(null);
  const [selectedManger, setSelectedManger] = useState<OrganizationManager>();
  const { communities = [] } = useCommunities();
  const { setAlertText, setSeverity, setTime } = useAlert();
  const { currentUser } = useUser();
  const question = useQuestion();
  const form = useFormDialog();
  const { mutate: addEditManger, isPending } = useAddEditManager();

  const where = useMemo(() => {
    return {
      organization: { $id: currOrganization?.id ?? "" },
    };
  }, [currOrganization?.id]);

  async function handleManagerDelete(
    whereToUpdate: EntityFilter<OrganizationManager>,
  ) {
    if (!currentUser) return;
    try {
      // Todo: remove managers from teams
      HyperTable.context.current?.refetch("remove");
      HyperTable.context.current?.setAllSelected(false);
    } catch (err) {
      // eslint-disable-next-line no-console -- error handling
      console.error(err);
      setAlertText("קרתה תקלה אנא נסה שנית");
      setSeverity("warning");
      setTime(2000);
    }
  }

  const getMenuActions = useCallback<SingleRowActionGetter<OrganizationManager>>(
    (row) => {
      return [{
        text: "שיוך לפרויקטים",
        infoTooltip: "שיוך מנהל זה לפרויקטים",
        icon: < Groups2Icon />,
        onClick: (_, row) => {
          setSelectedManger(row.original);
          return {
            refetch: false,
          };
        },
      },
      {
        icon: <EditIcon />,
        text: "עריכה",
        onClick: async (e, row) => {
          editManagerPopup({
            ...row.original,
            phoneNumber: formatPhoneNumber(row.original.phoneNumber),
          });
        },
      },
      ];
    }, []);

  const handleAdminCheckboxChange = async (
    isAdmin: boolean,
    whereToUpdate: EntityFilter<OrganizationManager>,
  ) => {
    // TODO: remove this from Auth.controller and to table actions
    await AuthController.AdminRolesChange(isAdmin, whereToUpdate);
    setAlertText("המנהל עודכן בהצלחה");
    setSeverity("success");
  };

  const handleTeamsChange = async (
    manager: OrganizationManager,
    teams: string[],
  ) => {
    const filterTeams = teams?.filter(
      (team) => !manager?.teams?.map((team) => team.teamId).includes(team),
    );
    const teamsToAdd = (communities ?? [])
      .filter((team) => filterTeams.includes(team.id))
      .map((team) => ({ team }));
    await organizationManagerRepo.relations(manager).teams.insert(teamsToAdd);
    await organizationManagerRepo
      .relations(manager)
      .teams.deleteMany({ where: { team: { $id: { $nin: teams } } } });
    HyperTable.context.current?.refetch("remove");
    HyperTable.context.current?.table.toggleAllRowsSelected(false);
  };

  const getButtons = useCallback<ActionButtonsGetter<OrganizationManager>>(
    ({ selectedCount }) => {
      if (selectedCount > 1) {
        return [
          {
            icon: <Groups2Icon />,
            onClick: () => {
              // Store selected managers for team assignment
              const selectedManagers = HyperTable.context.current?.table.getSelectedRowModel().rows.map(row => row.original) || [];
              setSelectedManger(selectedManagers[0]); // Use first one as representative
              return {
                refetch: false,
                resetSelection: false,
              };
            },
            title: "שיוך לפרויקט",
          },
          {
            icon: <DeleteOutlineRoundedIcon />,
            onClick: async ({ context, event, whereToUpdate }) => {
              const yes = await question("האם ברצונך למחוק מנהלים אלו?");
              if (yes) {
                await handleManagerDelete(whereToUpdate);
                setAlertText("המנהלים נמחקו בהצלחה");
                setSeverity("success");
                setTime(2000);
                return {
                  refetch: true,
                  resetSelection: true,
                };
              }
            },
            title: "מחיקה",
          },
          {
            icon: <FileUploadIcon />,
            onClick: async ({ whereToUpdate }) => {
              const excelResult =
                await ExcelController.exportManagersToExcel(whereToUpdate);
              if (excelResult) {
                downloadExcelFile(excelResult);
              }

              return {
                refetch: false,
                resetSelection: true,
              };
            },
            title: "ייצוא לאקסל",
          },
        ];
      }

      return [
        {
          icon: <PersonPinRoundedIcon />,
          onClick: async ({ whereToUpdate }) => {
            await handleAdminCheckboxChange(false, whereToUpdate);
            return {
              refetch: true,
              resetSelection: true,
            };
          },
          title: "הסר מניהול כללי",
        },
        {
          icon: <PersonPinRoundedIcon />,
          onClick: async ({ whereToUpdate }) => {
            await handleAdminCheckboxChange(true, whereToUpdate);
            return {
              refetch: true,
              resetSelection: true,
            };
          },
          title: "הגדר כמנהל/ת כללי",
        },
        {
          icon: <DeleteOutlineRoundedIcon />,
          onClick: async ({ whereToUpdate }) => {
            const yes = await question("האם ברצונך למחוק מנהלים אלו?");
            if (yes) {
              await handleManagerDelete(whereToUpdate);
              setAlertText("המנהל/ת נמחק בהצלחה");
              setSeverity("success");
              setTime(2000);
              return {
                refetch: true,
                resetSelection: true,
              };
            }
          },
          title: "מחיקה",
        },
        {
          icon: <FileUploadIcon />,
          onClick: async ({ whereToUpdate }) => {
            const excelResult =
              await ExcelController.exportManagersToExcel(whereToUpdate);
            if (excelResult) {
              downloadExcelFile(excelResult);
            }

            return {
              refetch: false,
              resetSelection: true,
            };
          },
          title: "ייצוא לאקסל",
        },
      ];
    },
    [question, handleManagerDelete, handleAdminCheckboxChange],
  );

  const addManagerPopup = () => {
    form({
      loading: isPending,
      title: "הוספת מנהל",
      fields: {
        name: {
          name: "name",
          type: "text",
          caption: "שם המנהל",
        },
        phoneNumber: {
          type: "text",
          caption: "טלפון",
          name: "phoneNumber",
        },
        role: {
          type: "text",
          caption: "תפקיד",
          name: "role",
        },
        voicenterExtension: {
          type: "text",
          caption: "Voicenter - מזהה ל־Click2Call (שלוחה / קוד עובד / מזהה)",
          name: "voicenterExtension",
        },
        teams: {
          type: "autocomplete",
          caption: "פרויקטים",
          name: "teams",
          options: communities?.map((com) => ({
            value: com.id,
            content: com.name,
            key: com.id,
          })),
          required: true,
        },
      },
      defaultValues: {
        phoneNumber: "",
        teams: "",
        name: "",
        role: "",
        voicenterExtension: "",
      },
      onOk: (value) => {
        addEditManger({ ...value, hyperTable: HyperTable });
      },
      validationSchema: yup.object().shape({
        name: yup
          .string()
          .min(2, "השם צריך להכיל לפחות 2 תווים")
          .max(20, "השם יכול להכיל עד 20 תווים")
          .required("שדה זה הינו שדה חובה"),
        teams: yup.string().required("חובה לבחור צוות אחד לפחות"),
        phoneNumber: yup.string().required().length(10, "חייב להכיל 10 ספרות"),
        role: yup.string().required(),
        voicenterExtension: yup
          .string()
          .max(64, "עד 64 תווים")
          .trim()
          .default(""),
      }),
    });
  };

  const editManagerPopup = (values: any) => {
    form({
      loading: isPending,
      title: "עריכת מנהל",
      fields: {
        name: {
          name: "name",
          type: "text",
          caption: "שם המנהל",
        },
        phoneNumber: {
          type: "text",
          caption: "טלפון",
          name: "phoneNumber",
          disabled: true,
        },
        role: {
          type: "text",
          caption: "תפקיד",
          name: "role",
        },
        voicenterExtension: {
          type: "text",
          caption: "Voicenter - מזהה ל־Click2Call (שלוחה / קוד עובד / מזהה)",
          name: "voicenterExtension",
        },
      },
      defaultValues: {
        ...values,
        phoneNumber: formatPhoneNumber(values.phoneNumber),
        voicenterExtension: values.voicenterExtension?.trim() ?? "",
      },
      onOk: async (value) => {
        const ext = value.voicenterExtension?.trim();
        await organizationManagerRepo.update(values.id, {
          name: value.name,
          role: value.role,
          voicenterExtension: ext || null,
        });
        HyperTable.context.current?.refetch("remove");
        setAlertText("המנהל עודכן בהצלחה");
        setSeverity("success");
      },
      validationSchema: yup.object().shape({
        name: yup
          .string()
          .min(2, "השם צריך להכיל לפחות 2 תווים")
          .max(20, "השם יכול להכיל עד 20 תווים")
          .required("שדה זה הינו שדה חובה"),
        role: yup.string().required("חובה למלא תפקיד"),
        phoneNumber: yup.string().required(),
        voicenterExtension: yup
          .string()
          .max(64, "עד 64 תווים")
          .trim()
          .default(""),
      }),
    });
  };

  if (!currOrganization)
    return (
      <Stack width="100%" height="100%" paddingTop="10vh" alignItems="center">
        <CircularProgress />
      </Stack>
    );

  return (
    <HyperTable
      tableId="managers-table"
      findConfig={{
        where,
        enabled: !!currOrganization,
        include: { teams: { include: { team: true } } },
        initialOrderBy: { name: "asc" },
      }}
      components={{
        addRowButton: {
          onClick: addManagerPopup,
          content: "הוספת מנהל/ת",
        },
      }}
      onRowClick={(_, row) => { }}
      title={<TableHeaderTitles titles={[{ content: "מנהלים" }]} />}
    >
      <HyperTable.Toolbar>
        {filters.map((filter) =>
          isEntityFilter(filter) ? (
            <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
          ) : (
            <HyperTable.CustomFilter key={filter.id} {...(filter as any)} />
          ),
        )}
      </HyperTable.Toolbar>
      {getColumns(setSelectedManger).map((column) => (
        <HyperTable.Column key={column.field} {...column} />
      ))}
      <HyperTable.SelectColumn />
      <HyperTable.MenuColumn actions={getMenuActions} />
      <HyperTable.ActionsBar buttons={getButtons} />
      <TeamsSelectDialog
        onClose={() => {
          setSelectedManger(undefined);
        }}
        onSave={async (teams: (string | Team)[]) => {
          if (!selectedManger) return;
          // Apply team changes to all selected managers
          await handleTeamsChange(selectedManger, teams as string[]);

          setAlertText("ההרשאות עודכנו בהצלחה");
          setSeverity("success");
          setSelectedManger(undefined);
        }}
        open={!!selectedManger}
        value={selectedManger?.teams?.map((team) => team.teamId)}
      />
    </HyperTable>
  );
};

export default ManagersTable;
